"use client";

import type { FooterData, FooterGroup, LanguageOption } from "../../../types";
import { RuntimeImage, useLocale } from "../../../runtime/RendererRuntime";
import { GuardedLink, useDevPlaceholder } from "../../../runtime/DevPlaceholder";
import { t } from "../../../i18n/strings";
import { resolveConfiguredHref } from "../../../utils/links";
import { SocialDiscRow } from "./SocialDiscRow";
import { FooterPartners } from "./FooterPartners";
import { FooterLanguageLinks } from "./FooterLanguageLinks";

/**
 * Mobile footer — vertical stack mirroring the desktop content order so
 * editorial changes propagate to both viewports without per-viewport edits:
 *   1. Brand + addresses
 *   2. Visitez aussi nos pages (collapsible)
 *   3. Informations pratiques (collapsible)
 *   4. Contact note paragraph
 *   5. Yellow social row
 *
 * Audiences are intentionally not rendered on either viewport now; the field
 * remains in `FooterData` so other surfaces (or a future redesign) can use it.
 */
export function SiteFooterMobile({
	data,
	logoSrc,
	logoAlt,
	language,
	languageOptions,
	onLanguageChange,
	resolveHref,
}: {
	data: FooterData;
	logoSrc: string;
	logoAlt: string;
	language?: string;
	languageOptions?: LanguageOption[];
	onLanguageChange?: (code: string) => void;
	resolveHref?: (code: string) => string;
}) {
	const { previewMode } = useDevPlaceholder();
	const locale = useLocale();
	const resourcesGroup = pickGroup(data.groups, ["Visitez aussi nos pages", "Visitez aussi"], 0);
	const practicalGroup = pickGroup(data.groups, ["Informations pratiques"], 1);
	const contactNote = (data.contactNote ?? "").trim();
	const legalLinks = data.legalLinks.filter((link) => previewMode || Boolean(resolveConfiguredHref(link.href)));

	return (
		<div className="md:hidden">
			<div className="rounded-lg border border-white/10 bg-white/[0.04] p-4">
				<RuntimeImage src={logoSrc} alt={logoAlt} className="h-auto w-auto max-w-[160px] object-contain" />
				<div className="mt-4 space-y-3 text-base xl:text-lg text-white/80">
					{data.addresses.map((addr, addressIndex) => (
						<div key={`${addr.title}-${addressIndex}`}>
							<p className="font-semibold text-white">{addr.title}</p>
							{addr.lines.map((l, idx) => (
								<p key={idx}>{l}</p>
							))}
							{(addr.actions ?? []).filter((a) => previewMode || resolveConfiguredHref(a.href)).map((a) => (
								<GuardedLink
									key={a.label}
									label={a.label}
									className="inline-block rounded-full bg-white/10 px-2 py-1 text-xs xl:text-sm hover:bg-white/15"
									href={a.href}
								>
									{a.label}
								</GuardedLink>
							))}
						</div>
					))}
				</div>
			</div>

			<div className="mt-4 space-y-3">
				<MobileGroup group={resourcesGroup} fallbackTitle="Visitez aussi nos pages" />
				<MobileGroup group={practicalGroup} fallbackTitle="Informations pratiques" />
			</div>

			{contactNote && (
				<p className="mt-4 rounded-lg border border-white/10 bg-white/[0.04] p-4 text-sm xl:text-base leading-relaxed text-white/90">
					{contactNote}
				</p>
			)}

			{/* Mobile-only: center the social row and give it generous top/bottom
			    padding so it doesn't feel cramped between the contact note and the
			    legal line. Desktop keeps left-aligned, tighter spacing — see
			    SiteFooterDesktop.tsx. */}
			<div className="mt-2 py-6">
				<SocialDiscRow items={data.socials} className="justify-center" />
			</div>

			{legalLinks.length > 0 && (
				<nav aria-label="Informations légales" className="mt-4 border-t border-white/20 pt-5">
					<ul className="flex flex-wrap justify-center gap-x-4 gap-y-3 text-center text-xs font-semibold uppercase tracking-wide text-white/65">
						{legalLinks.map((link) => (
							<li key={link.label}>
								<GuardedLink label={link.label} href={link.href} className="inline-block py-1 transition hover:text-white">
									{link.label}
								</GuardedLink>
							</li>
						))}
					</ul>
				</nav>
			)}

			{/* Ownership and production credits close the page. */}
			<div className="mt-5 flex flex-col items-center gap-4 text-center text-xs xl:text-sm text-white/70">
				<p>© {new Date().getFullYear()} APA Parts. {t(locale, "allRightsReserved")}</p>
				<FooterPartners />
				{languageOptions && onLanguageChange ? (
					<FooterLanguageLinks
						language={language ?? ""}
						languageOptions={languageOptions}
						onLanguageChange={onLanguageChange}
						resolveHref={resolveHref}
					/>
				) : null}
			</div>
		</div>
	);
}

/** Collapsible group section with the same chrome as the original mobile
 *  footer. Hidden when the group has no links. */
function MobileGroup({ group, fallbackTitle }: { group: FooterGroup | null; fallbackTitle: string }) {
	const { previewMode } = useDevPlaceholder();
	const links = (group?.links ?? []).filter((link) => previewMode || resolveConfiguredHref(link.href));
	if (!group || links.length === 0) return null;
	return (
		<details className="group overflow-hidden rounded-lg border border-white/10 bg-white/[0.04]" open>
			<summary className="flex cursor-pointer list-none items-center justify-between px-4 py-3">
				<span className="apa-eyebrow apa-eyebrow-light">{group.title || fallbackTitle}</span>
				<span className="grid h-8 w-8 place-items-center rounded border border-white/20 bg-white/10" aria-hidden="true">
					<svg className="h-4 w-4 transition-transform group-open:rotate-180" viewBox="0 0 20 20" fill="none">
						<path d="m5 7.5 5 5 5-5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
					</svg>
				</span>
			</summary>
			<ul className="space-y-3 border-t border-white/10 px-4 py-4 text-base xl:text-lg text-white/90">
				{links.map((link) => (
					<li key={link.label}>
						<GuardedLink label={link.label} className="block hover:underline" href={link.href}>
							{link.label}
						</GuardedLink>
					</li>
				))}
			</ul>
		</details>
	);
}

function pickGroup(groups: FooterGroup[], titles: string[], fallbackIndex: number): FooterGroup | null {
	for (const title of titles) {
		const match = groups.find((g) => g.title === title);
		if (match) return match;
	}
	return groups[fallbackIndex] ?? null;
}
